import { useMutation } from '@apollo/client/react';
import {
  type CompleteStylistFragment,
  SnoozeReminderDocument,
  StartServiceDocument,
} from '@drycut/graphql';
import { ReminderSound } from '@drycut/sound';
import { Button, Heading, Stack } from '@drycut/ui-components';
import { useState } from 'react';
import { useBoolean, useInterval } from 'usehooks-ts';

type Props = {
  stylist: CompleteStylistFragment;
};

export function ServiceStartReminder({ stylist }: Props): React.ReactNode {
  const { value: soundPlayed, setTrue: setSoundPlayed, setFalse: resetSound } = useBoolean(false);
  const [pastReminder, setPastReminder] = useState<boolean>(false);

  const serviceExecution = stylist.currentServiceExecution;

  const [startService, { loading: starting }] = useMutation(StartServiceDocument, {
    variables: { input: { serviceExecutionId: serviceExecution?.id || '' } },
  });

  const [snoozeReminder, { loading: snoozing }] = useMutation(SnoozeReminderDocument, {
    variables: { input: { serviceExecutionId: serviceExecution?.id || '' } },
  });

  const handleSnooze = (event: React.MouseEvent) => {
    event.stopPropagation();
    snoozeReminder();
    setPastReminder(false);
    resetSound();
  };

  const handleStart = (event: React.MouseEvent) => {
    event.stopPropagation();
    startService();
  };

  useInterval(() => {
    const alarmTime = serviceExecution?.startReminderAt;

    if (!alarmTime) {
      setPastReminder(false);
      return;
    }

    const isPastReminder = Date.parse(alarmTime) <= new Date().getTime();
    setPastReminder(isPastReminder);
  }, 500);

  if (!pastReminder) {
    return null;
  }

  const loading = starting || snoozing;

  return (
    <Stack className="absolute bg-primary-600 bg-opacity-95 shadow w-64 mt-14">
      {pastReminder && !soundPlayed && <ReminderSound onPlay={setSoundPlayed} />}

      <Stack className="m-5 text-white" gap={5}>
        <Heading variant="h4" className="text-xl leading-6">
          Kunde noch nicht erschienen?
        </Heading>

        <Stack direction="row" className="justify-between">
          <Button
            fill="outlined"
            onClick={handleSnooze}
            disabled={loading}
            color="secondary"
            className="bg-white border-0 outline-none"
          >
            Nein
          </Button>

          <Button
            className="bg-black/50 border-0 outline-none"
            onClick={handleStart}
            disabled={loading}
          >
            Start
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
